// Conversations per dialog link: who talked, who submitted, and — the point —
// who walked away leaving a recoverable draft. Renders under each dialog row
// in DialogView (active and closed alike: closed links are the main straggler
// case while their drafts are inside the 7-day memory window).

import { useState } from 'react';
import { ChevronDown, ChevronRight, Loader2, Plus } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import { useDialogSessions, useSessionDraft, usePromoteSession } from '@/hooks/use-dialog';
import type { DialogSessionRow } from '@/services/api';

const STATUS_STYLE: Record<DialogSessionRow['status'], string> = {
  'in progress': 'bg-blue-100 text-blue-800',
  unsubmitted: 'bg-amber-100 text-amber-800',
  submitted: 'bg-green-100 text-green-800',
  promoted: 'bg-secondary text-secondary-foreground',
};

export function SessionsSection({ dialogId, projectId, className }: { dialogId: string; projectId: string; className?: string }) {
  const { data: sessions } = useDialogSessions(dialogId, projectId);
  if (!sessions || sessions.length === 0) return null;

  const submitted = sessions.filter((s) => s.status === 'submitted' || s.status === 'promoted').length;
  const unsubmitted = sessions.length - submitted;

  return (
    <div className={cn('mt-2 border-t pt-2', className)}>
      <p className="text-xs text-muted-foreground mb-1.5">
        {sessions.length} conversation{sessions.length !== 1 ? 's' : ''} · {submitted} submitted
        {unsubmitted > 0 ? ` · ${unsubmitted} unsubmitted` : ''}
      </p>
      <ul className="space-y-1">
        {sessions.map((s) => (
          <SessionRow key={s.actorId} session={s} dialogId={dialogId} projectId={projectId} />
        ))}
      </ul>
    </div>
  );
}

function SessionRow({ session, dialogId, projectId }: { session: DialogSessionRow; dialogId: string; projectId: string }) {
  const [expanded, setExpanded] = useState(false);
  const showDraft = session.status === 'unsubmitted' || session.status === 'in progress';
  const { data: draft, isLoading } = useSessionDraft(dialogId, session.actorId, projectId, expanded && showDraft);
  const promote = usePromoteSession();

  const rowContent = (
    <>
      {showDraft ? (
        expanded ? <ChevronDown className="h-3 w-3 shrink-0" /> : <ChevronRight className="h-3 w-3 shrink-0" />
      ) : (
        <span className="w-3 shrink-0" />
      )}
      <span className="truncate">{session.label}</span>
      <span className={cn('px-1.5 py-0.5 rounded text-[10px] shrink-0', STATUS_STYLE[session.status])}>
        {session.status}
      </span>
      {/* Draft rows count what's recoverable; settled rows count what was
          actually contributed (absent on rows settled before the field existed). */}
      <span className="text-muted-foreground ml-auto shrink-0">
        {showDraft
          ? `${session.draftItemCount} item${session.draftItemCount !== 1 ? 's' : ''}`
          : session.submittedItemCount !== undefined
            ? `${session.submittedItemCount} added`
            : null}
      </span>
    </>
  );

  return (
    <li className="text-xs">
      {showDraft ? (
        <button
          onClick={() => setExpanded(!expanded)}
          aria-expanded={expanded}
          className="w-full flex items-center gap-2 py-1 text-left hover:bg-muted/50 rounded"
        >
          {rowContent}
        </button>
      ) : (
        <div className="w-full flex items-center gap-2 py-1 text-left">
          {rowContent}
        </div>
      )}
      {expanded && showDraft && (
        <div className="ml-5 mt-1 mb-2 space-y-1">
          {isLoading ? (
            <Loader2 className="h-3 w-3 animate-spin text-muted-foreground" />
          ) : !draft ? (
            <p className="text-muted-foreground">
              Draft expired — conversations are kept for 7 days.
            </p>
          ) : (
            <>
              {draft.items.map((item) => (
                <div key={item.id} className="flex gap-2">
                  <span className="uppercase text-[9px] tracking-wide text-muted-foreground shrink-0">{item.type}</span>
                  <div className="min-w-0">
                    <span className="leading-snug">{item.title}</span>
                    {item.description && (
                      <p className="text-muted-foreground leading-snug">{item.description}</p>
                    )}
                  </div>
                </div>
              ))}
              {/* Promote only once the conversation has gone quiet: it ends
                  the session (submit and re-promote both refuse afterwards),
                  so a live respondent's later turns would be stranded. */}
              {session.status === 'unsubmitted' ? (
                <>
                  <Button
                    size="sm"
                    variant="outline"
                    className="mt-1 h-6 text-xs"
                    disabled={promote.isPending}
                    onClick={() => promote.mutate({ dialogId, actorId: session.actorId, projectId })}
                  >
                    {promote.isPending ? <Loader2 className="h-3 w-3 animate-spin mr-1" /> : <Plus className="h-3 w-3 mr-1" />}
                    Add to graph
                  </Button>
                  {promote.isError && <p className="text-destructive">{(promote.error as Error).message}</p>}
                </>
              ) : (
                <p className="text-muted-foreground mt-1">
                  Still chatting. You can add these once the conversation goes quiet.
                </p>
              )}
            </>
          )}
        </div>
      )}
    </li>
  );
}
